import { richTextFirstBlockLabel, richTextToPlainLabel } from '@/lib/rich-text';
import { DividerBlock, TableBlock, TestBlock } from '@/types/report-payload';
import { stripSplitBlockId } from '../pagination/utils';
import { findBlockIndexByRealId, getBlockRealId, reindexBlocks } from './blockClipboard';

/**
 * Positional grouping for the report's flat `tests.blocks` array.
 *
 * `linked_table_id` is the only grouping field in the payload, but roughly half
 * the block producers never set it — the toolbox factory, dividers (which have
 * no such field), images, the DOCX importer and consolidations all emit
 * unlinked blocks. Scoping the sidebar to that link would leave half the
 * document outside every group, so the sidebar groups by what the reader
 * actually sees on the page instead: a table and the contiguous run of blocks
 * beneath it, up to the next table or divider.
 *
 * The link-based counterpart lives in `blockClipboard.ts` (`resolveInsertionIndex`,
 * `isLinkedCompanionOf`) and still governs copy/paste and delete cascades.
 */

export const UNSCOPED_GROUP_LABEL = 'Blocos avulsos';

export interface BlockScope {
    /** Real id of the run's anchor table, or `null` for an anchorless run. */
    anchorRealId: string | null;
    label: string;
    /** Index a new block should be spliced at to land at the end of the run. */
    insertIndex: number;
}

const isGroupBoundary = (block: TestBlock): boolean =>
    block.block_type === 'table' || block.block_type === 'divider';

/**
 * Walks back from `fromIndex` to the table that owns that position. Returns -1
 * when a divider or the start of the array is reached first, which means the
 * block sits in a run with no instrument at its head.
 */
export const findGroupAnchorIndex = (blocks: TestBlock[], fromIndex: number): number => {
    if (fromIndex < 0 || fromIndex >= blocks.length) return -1;

    for (let index = fromIndex; index >= 0; index -= 1) {
        const block = blocks[index];
        if (block.block_type === 'table') return index;
        if (block.block_type === 'divider') return -1;
    }

    return -1;
};

/**
 * First index *after* the run that starts at `startIndex` — i.e. where a new
 * block must be spliced to become the run's last member.
 */
export const findGroupEndIndex = (blocks: TestBlock[], startIndex: number): number => {
    if (startIndex < 0) return blocks.length;

    let index = Math.min(startIndex, blocks.length - 1) + 1;
    while (index < blocks.length && !isGroupBoundary(blocks[index])) {
        index += 1;
    }

    return index;
};

/**
 * What an instrument is called: the first block of its table's caption.
 *
 * The caption is rich text, and this is the one place that turns it into a
 * label — the breadcrumb, the battery list, the panel header and the rename
 * field all read from here, so the stripping and the unnamed fallback are
 * defined once. Only the first block, because the paragraphs a psychologist
 * writes under the name are the instrument's description, not part of what it
 * is called.
 */
export const getInstrumentLabel = (block: TableBlock): string =>
    richTextFirstBlockLabel(block.description) || 'Instrumento sem nome';

/**
 * Names the run for the breadcrumb: the anchor table's description, else the
 * divider heading the anchorless run, else a neutral label.
 */
export const getGroupLabel = (blocks: TestBlock[], anchorIndex: number): string => {
    if (anchorIndex >= 0) return getInstrumentLabel(blocks[anchorIndex] as TableBlock);

    return UNSCOPED_GROUP_LABEL;
};

/** Label for the run containing `fromIndex` when it has no anchor table. */
const getAnchorlessLabel = (blocks: TestBlock[], fromIndex: number): string => {
    for (let index = fromIndex; index >= 0; index -= 1) {
        const block = blocks[index];
        if (block.block_type !== 'divider') continue;

        const title = richTextToPlainLabel((block as DividerBlock).title);
        return title || UNSCOPED_GROUP_LABEL;
    }

    return UNSCOPED_GROUP_LABEL;
};

/**
 * Resolves a selected block into the scope the sidebar drills into. Returns
 * null when the id no longer exists, which is how the panel knows to pop back
 * to its top level after the anchor is deleted.
 */
export const resolveBlockScope = (blocks: TestBlock[], realId: string): BlockScope | null => {
    // Callers hand us ids straight off rendered blocks, which may be split
    // fragments (`<id>__split__2`) on a paginated table.
    const blockIndex = findBlockIndexByRealId(blocks, stripSplitBlockId(realId));
    if (blockIndex === -1) return null;

    const anchorIndex = findGroupAnchorIndex(blocks, blockIndex);

    if (anchorIndex === -1) {
        return {
            anchorRealId: getBlockRealId(blocks[blockIndex]),
            label: getAnchorlessLabel(blocks, blockIndex),
            insertIndex: findGroupEndIndex(blocks, blockIndex),
        };
    }

    return {
        anchorRealId: getBlockRealId(blocks[anchorIndex]),
        label: getGroupLabel(blocks, anchorIndex),
        insertIndex: findGroupEndIndex(blocks, anchorIndex),
    };
};

/**
 * Insertion index for a scope that was resolved earlier: the anchor may have
 * moved or been deleted since, so it is looked up again rather than trusted.
 * A null anchor (the sidebar's "blocos avulsos" entry) appends at the end.
 */
export const resolveScopeInsertIndex = (
    blocks: TestBlock[],
    anchorRealId: string | null,
): number => {
    if (!anchorRealId) return blocks.length;

    const anchorIndex = findBlockIndexByRealId(blocks, stripSplitBlockId(anchorRealId));
    if (anchorIndex === -1) return blocks.length;

    return findGroupEndIndex(blocks, anchorIndex);
};

/**
 * Every real id in the run anchored at `anchorRealId`, in document order — the
 * preview marks the whole run so the reader can see which instrument the panel
 * is currently talking about. A group has no DOM ancestor of its own (and
 * pagination can spread it over several pages), so the highlight has to be
 * applied member by member.
 *
 * Resolved from the live block list on every call, never cached on the scope:
 * the run grows and shrinks under the panel as blocks are added or deleted.
 * Empty for the sidebar's anchorless entry and for an anchor that is gone.
 */
export const resolveGroupMemberIds = (
    blocks: TestBlock[],
    anchorRealId: string | null,
): string[] => {
    if (!anchorRealId) return [];

    const anchorIndex = findBlockIndexByRealId(blocks, stripSplitBlockId(anchorRealId));
    if (anchorIndex === -1) return [];

    return blocks
        .slice(anchorIndex, findGroupEndIndex(blocks, anchorIndex))
        .map((block) => getBlockRealId(block));
};

/**
 * Every group in the section, in document order — the sidebar's top level uses
 * this to list the battery already in the laudo, so an instrument is reachable
 * without hunting for it on the page.
 *
 * A run with no anchor table (blocks under a divider, or before the first
 * table) is listed too, labelled by its divider, because those blocks would
 * otherwise be invisible to the panel.
 */
export interface BlockGroupSummary extends BlockScope {
    /** How many blocks the run holds, the anchor included. */
    blockCount: number;
    /** Real id of the first block, for scrolling the group into view. */
    firstRealId: string;
}

/** Half-open `[startIndex, endIndex)` of one group's run in the flat array. */
interface BlockGroupRange {
    startIndex: number;
    endIndex: number;
}

/**
 * The single walk both `listBlockGroups` and `moveBlockGroup` read from — one
 * definition of where a group starts and stops, so the list and the move that
 * reorders it cannot drift apart.
 *
 * The ranges partition the array: a divider heads no group and belongs to none,
 * every other block belongs to exactly one run, and a run never spans a divider
 * (`findGroupEndIndex` stops there). That is what makes "move a run and leave
 * the dividers where they are" a well-defined operation.
 */
const listGroupRanges = (blocks: TestBlock[]): BlockGroupRange[] => {
    const ranges: BlockGroupRange[] = [];
    let index = 0;

    while (index < blocks.length) {
        // A divider heads a part of the laudo, not an instrument; it is not a
        // group of its own, but the run after it may be anchorless.
        if (blocks[index].block_type === 'divider') {
            index += 1;
            continue;
        }

        const endIndex = findGroupEndIndex(blocks, index);
        ranges.push({ startIndex: index, endIndex });
        index = Math.max(endIndex, index + 1);
    }

    return ranges;
};

export const listBlockGroups = (blocks: TestBlock[]): BlockGroupSummary[] =>
    listGroupRanges(blocks).map(({ startIndex, endIndex }) => {
        const block = blocks[startIndex];
        const isAnchored = block.block_type === 'table';

        return {
            anchorRealId: getBlockRealId(block),
            label: isAnchored
                ? getGroupLabel(blocks, startIndex)
                : getAnchorlessLabel(blocks, startIndex),
            insertIndex: endIndex,
            blockCount: endIndex - startIndex,
            firstRealId: getBlockRealId(block),
        };
    });

/**
 * Reorders the battery: moves the group at `fromGroupIndex` to
 * `toGroupIndex`, in the same index space `listBlockGroups` returns — the
 * sidebar's "Instrumentos já adicionados" list.
 *
 * **The dividers do not travel.** The section is read as
 * `P0 G1 P1 G2 … Gn Pn` (`Gi` a group's run, `Pi` the divider-only gap between
 * two of them) and rebuilt with the runs permuted and every gap left in its own
 * slot. So `[t1, divisor, t2]` with the two instruments swapped comes back as
 * `[t2, divisor, t1]`: the heading stays where the author put it and the
 * instruments change section around it, which is what the panel's list order
 * promises. Splicing the run out and reinserting it would instead drag the
 * divider to the top and empty the first section.
 *
 * Deliberately *not* `moveBlocksToIndex`: that one moves a table's
 * `linked_table_id` companions wherever they live, which for this list would
 * silently teleport a chart the user parked under a different instrument. The
 * run is what the row names, so the run is what moves.
 *
 * Note the runs are positional, so are their labels: an anchorless run dragged
 * out from under a divider is renamed by whichever divider now precedes it.
 */
export const moveBlockGroup = (
    blocks: TestBlock[],
    fromGroupIndex: number,
    toGroupIndex: number,
): TestBlock[] => {
    if (fromGroupIndex === toGroupIndex) return blocks;

    const ranges = listGroupRanges(blocks);
    if (!ranges[fromGroupIndex] || !ranges[toGroupIndex]) return blocks;

    const runs = ranges.map(({ startIndex, endIndex }) => blocks.slice(startIndex, endIndex));
    // `gaps[i]` is what sits *before* run `i`; the last one is the tail.
    const gaps = [
        blocks.slice(0, ranges[0].startIndex),
        ...ranges.map((range, index) =>
            blocks.slice(range.endIndex, ranges[index + 1]?.startIndex ?? blocks.length),
        ),
    ];

    const order = runs.map((_, index) => index);
    order.splice(toGroupIndex, 0, ...order.splice(fromGroupIndex, 1));

    const next = [...gaps[0]];
    order.forEach((runIndex, slot) => {
        next.push(...runs[runIndex], ...gaps[slot + 1]);
    });

    const unchanged =
        next.length === blocks.length && next.every((block, index) => block === blocks[index]);
    if (unchanged) return blocks;

    // `order` is rewritten, ids are not: comments and measurements are keyed by
    // block id, so a move must never remint one.
    return reindexBlocks(next);
};
